package com.kinoshazam.screens.search.components

import androidx.compose.animation.core.FastOutSlowInEasing
import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.animation.core.tween
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableIntStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.layout.onSizeChanged
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.dp
import androidx.compose.ui.util.lerp
import androidx.compose.ui.zIndex
import com.kinoshazam.uiKit.theme.UiKitTheme
import kotlin.math.roundToInt

private const val SwitchDurationMs = 560

@Composable
fun SearchModeSwitcher(
    identifyMode: Boolean,
    identifyEnabled: Boolean,
    modifier: Modifier = Modifier,
    wandSize: Dp = 44.dp,
    gap: Dp = 8.dp,
    titleContent: @Composable () -> Unit,
    identifyContent: @Composable () -> Unit,
    wand: @Composable () -> Unit,
) {
    if (!identifyEnabled) {
        Box(modifier.fillMaxWidth()) {
            titleContent()
        }
        return
    }

    val progress by animateFloatAsState(
        targetValue = if (identifyMode) 1f else 0f,
        animationSpec = tween(
            durationMillis = SwitchDurationMs,
            easing = FastOutSlowInEasing,
        ),
        label = "search_mode_wipe",
    )
    val density = LocalDensity.current
    var titleHeightPx by remember { mutableIntStateOf(0) }
    var identifyHeightPx by remember { mutableIntStateOf(0) }

    BoxWithConstraints(modifier.fillMaxWidth()) {
        val wandPx = with(density) { wandSize.toPx() }
        val gapPx = with(density) { gap.toPx() }
        val totalPx = constraints.maxWidth.toFloat()
        val fieldWidthPx = (totalPx - wandPx - gapPx).coerceAtLeast(0f)
        val restWandX = fieldWidthPx + gapPx
        val wandX = lerp(restWandX, 0f, progress)
        val identifyX = wandX + wandPx + gapPx
        val identifyW = (totalPx - identifyX).coerceAtLeast(0f)
        val showTitle = progress < 0.995f
        val heightPx = lerp(
            (if (titleHeightPx == 0) wandPx else titleHeightPx.toFloat()),
            (if (identifyHeightPx == 0) wandPx else identifyHeightPx.toFloat()),
            progress,
        ).coerceAtLeast(wandPx)
        val wandY = ((heightPx - wandPx) / 2f).coerceAtLeast(0f)

        Box(
            modifier = Modifier
                .fillMaxWidth()
                .height(with(density) { heightPx.toDp() }),
        ) {
            if (showTitle) {
                Box(
                    modifier = Modifier
                        .align(Alignment.TopStart)
                        .width(with(density) { fieldWidthPx.toDp() })
                        .onSizeChanged { titleHeightPx = it.height }
                        .zIndex(0f),
                ) {
                    titleContent()
                }
            }

            if (identifyW > 1f) {
                Box(
                    modifier = Modifier
                        .align(Alignment.TopStart)
                        .offset { IntOffset(identifyX.roundToInt(), 0) }
                        .width(with(density) { identifyW.toDp() })
                        .fillMaxHeight()
                        .background(UiKitTheme.colors.color_3)
                        .onSizeChanged { identifyHeightPx = it.height }
                        .zIndex(1f),
                ) {
                    identifyContent()
                }
            }

            Box(
                modifier = Modifier
                    .offset { IntOffset(wandX.roundToInt(), wandY.roundToInt()) }
                    .size(wandSize)
                    .zIndex(2f)
                    .clip(CircleShape)
                    .background(UiKitTheme.colors.color_4),
                contentAlignment = Alignment.Center,
            ) {
                wand()
            }
        }
    }
}
